import { Feature, GetRevenueIssuesParams, type Issue, IssueType, parseDateString, toDateString } from "@pentacode/core";
import { activeIcon } from "@pentacode/ui/components";
import { LitElement, css, html, nothing } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import "../../elements/date-picker";
import type { DatePicker } from "../../elements/date-picker";
import { app, router } from "../../init";
import { canAccess, menuItemState } from "../../lib/route-tree";
import { Routing, routeProperty } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { mixins, shared } from "../../styles";
import { contactSupport } from "../../vendor/helpscout";
import { type RevenuesPage, revenuesPages, revenuesRoute } from "../app-routes";
import { routeAccess } from "../route-access";
import "./all";
import "./attribution";
import "./cashbook";
import "./daily";
import "./debtors";
import "./groups";

@customElement("ptc-revenues")
export class Revenues extends Routing(StateMixin(LitElement)) {
    routePattern = /^revenues(?:\/(?<page>\w+))?/;

    get helpPage() {
        switch (this._page) {
            case "all":
                return "/handbuch/umsaetze/alle_umsaetze";
            case "daily":
                return "/handbuch/umsaetze/tagesabrechnung";
            case "cashbook":
                return "/handbuch/umsaetze/kassenbuch";
            case "debtors":
                return "/handbuch/umsaetze/debitoren";
            case "groups":
                return "/handbuch/umsaetze/umsatzgruppen";
            case "attribution":
                return "/handbuch/umsaetze/erloeszuordnung";
            default:
                return "/handbuch/umsaetze";
        }
    }

    @routeProperty({ arg: "page" })
    private _page: RevenuesPage;

    @state()
    private _issues: Issue[] = [];

    @query("select")
    private _venueSelect: HTMLSelectElement;

    @query("ptc-date-picker")
    private _datePicker: DatePicker;

    private _selectVenue() {
        this.go(null, { ...this.router.params, venue: this._venueSelect.value });
    }

    handleRoute() {
        const venue = app.getVenue(this.venueId);
        if (!venue || !app.hasAccess({ venue })) {
            const venue = app.venues.find((venue) => app.hasAccess({ venue }));
            if (venue) {
                this.go(null, { ...this.router.params, venue: venue.id.toString() }, true);
            }
            return;
        }

        if (!revenuesPages.includes(this._page) || !this._canAccessPage(this._page)) {
            const page = revenuesPages.find((page) => this._canAccessPage(page));
            this.redirect(page ? `revenues/${page}` : "");
            return;
        }

        const today = toDateString(new Date());

        //Invalid date
        if (!this.date || !parseDateString(this.date) || this.date > today) {
            this.go(null, { ...this.router.params, date: today }, true);
            return;
        }

        this._datePicker && (this._datePicker.value = this.date);

        void this._loadIssues();
    }

    private _canAccessPage(page: RevenuesPage) {
        return canAccess(revenuesRoute(page), routeAccess());
    }

    private async _loadIssues() {
        // TODO: no DateRange restriction?
        this._issues = await app.api.getRevenueIssues(new GetRevenueIssuesParams({}));
    }

    private _getCalendarBadge(date: string) {
        if (
            this._issues.some((i) => {
                if (i.type === IssueType.UnbalancedDailyCash) {
                    return i.details.venueId.toString() === router.params.venue && i.date === date;
                }
                return false;
            })
        ) {
            return {
                icon: "icon.status.warning",
                class: "red",
            };
        } else if (
            this._issues.some(
                (i) =>
                    i.type === IssueType.PendingDailyRevenues &&
                    i.details.venueId.toString() === router.params.venue &&
                    i.date === date
            )
        ) {
            return {
                icon: "icon.action.edit",
                class: "orange",
            };
        }
    }

    static styles = [
        shared,
        css`
            :host {
                display: flex;
            }

            .sub-menu {
                position: relative;
                width: 220px;
                box-sizing: border-box;
                overflow: auto;
            }

            .sub-menu select {
                width: 100%;
                margin-bottom: 1em;
            }

            .main {
                flex: 1;
                min-width: 0;
                position: relative;
            }

            .main > * {
                ${mixins.fullbleed()};
            }

            @media print {
                .main > * {
                    position: static;
                }
            }
        `,
    ];

    private _renderButton(page: RevenuesPage) {
        const route = revenuesRoute(page);
        const state = menuItemState(route, routeAccess());
        if (state === "hidden") {
            return nothing;
        }

        const active = this._page === page;
        return html`
            <button
                ?active=${active}
                @click=${() => this.go(`revenues/${route.linkPath ?? page}`, this.router.params)}
                ?disabled=${state === "disabled"}
            >
                ${route.icon ? html`<ptc-ui-icon name=${activeIcon(route.icon, active)}></ptc-ui-icon>` : nothing}
                ${route.label}
            </button>
        `;
    }

    render() {
        if (!app.company || !app.company.features.includes(Feature.Revenues)) {
            return html`
                <div class="poster center-aligning center-justifying vertical layout fullbleed">
                    <ptc-ui-icon name="icon.feature.revenues"></ptc-ui-icon>

                    <div>
                        Dieser Bereich ist für Ihr Unternehmen noch nicht verfügbar! Bitte kontaktieren sie unser
                        Supportteam um zu erfahren, wie Sie dieses Modul freizuschalten können!
                    </div>

                    <button @click=${() => contactSupport("Umsatzmodul Freischalten")}>
                        <ptc-ui-icon name="icon.action.contact-support"></ptc-ui-icon>
                        Support Kontaktieren
                    </button>
                </div>
            `;
        }

        return html`
            <div class="sub-menu noprint">
                <div class="vertical tabs">${revenuesPages.map((page) => this._renderButton(page))}</div>

                <div class="divider"></div>

                <select @change=${this._selectVenue}>
                    ${app.accessibleVenues.map(
                        (venue) => html`
                            <option
                                .value=${venue.id.toString()}
                                ?selected=${venue.id.toString() === router.params.venue}
                                ?disabled=${!app.hasAccess({ venue })}
                            >
                                ${venue.name}
                            </option>
                        `
                    )}
                </select>

                <ptc-date-picker
                    class="small"
                    mode="day"
                    @change=${(e: CustomEvent) => this.go(null, { ...this.router.params, date: e.detail.date })}
                    .badge=${(date: string) => this._getCalendarBadge(date)}
                    .value=${this.date}
                    ?disabled=${this._page === "groups"}
                ></ptc-date-picker>
            </div>

            <div class="main">
                <ptc-revenues-all ?hidden=${this._page !== "all"}></ptc-revenues-all>

                <ptc-revenues-daily
                    ?hidden=${this._page !== "daily"}
                    @daily-revenues-saved=${this._loadIssues}
                ></ptc-revenues-daily>

                <ptc-revenues-cashbook ?hidden=${this._page !== "cashbook"}></ptc-revenues-cashbook>

                <ptc-revenues-debtors ?hidden=${this._page !== "debtors"}></ptc-revenues-debtors>

                <ptc-revenues-groups ?hidden=${this._page !== "groups"}></ptc-revenues-groups>

                <ptc-revenues-attribution ?hidden=${this._page !== "attribution"}></ptc-revenues-attribution>
            </div>
        `;
    }
}
